import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import PortalAvatar from '../components/profile/PortalAvatar';
import { useAuth } from '../context/AuthProvider';
import { apiFetch } from '../lib/api';
import { auth } from '../lib/firebase';
import { getPasswordPolicyError } from '../lib/passwordPolicy';

const Wrap = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 1.5rem 1rem;
  background:
    radial-gradient(1200px 340px at 80% -140px, rgba(116, 189, 255, 0.22), transparent 70%),
    linear-gradient(180deg, rgba(7, 13, 26, 0.98), rgba(10, 16, 30, 0.98));
`;

const JoinCard = styled(Card)`
  width: min(620px, 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  display: grid;
  gap: 1rem;
`;

const Header = styled.div`
  display: grid;
  gap: 0.3rem;

  h2 {
    margin: 0;
    color: #e8eaed;
  }

  p {
    margin: 0;
    color: #9aa4b2;
    font-size: 0.95rem;
  }
`;

const InviteMeta = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(14, 20, 34, 0.72);
  padding: 0.8rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.75rem;
`;

const Logo = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  overflow: hidden;
  display: grid;
  place-items: center;
  color: #cbd5e1;
  font-weight: 800;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MetaBody = styled.div`
  display: grid;
  gap: 0.32rem;
`;

const BusinessName = styled.p`
  margin: 0;
  color: #e8eaed;
  font-weight: 800;
`;

const MetaLine = styled.p`
  margin: 0;
  color: #a4afbe;
  font-size: 0.87rem;
`;

const InviterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(127, 214, 194, 0.35);
  background: rgba(61, 168, 145, 0.18);
  color: #baf9e8;
  font-size: 0.75rem;
  font-weight: 700;
`;

const Form = styled.form`
  display: grid;
  gap: 0.7rem;
`;

const FieldLabel = styled.label`
  display: grid;
  gap: 0.3rem;
  color: #9aa4b2;
  font-size: 0.82rem;
  font-weight: 700;
`;

const ReadOnlyInput = styled(Input)`
  opacity: 0.9;
`;

const Error = styled.p`
  margin: 0;
  color: #ff9b9b;
  font-weight: 700;
  font-size: 0.88rem;
`;

const Note = styled.p`
  margin: 0;
  color: #9aa4b2;
  font-size: 0.84rem;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

type InviteStatus = 'loading' | 'ready' | 'accepting' | 'accepted' | 'error';

type InvitePreview = {
  orgId: string;
  invitedEmail: string | null;
  invitedRole: 'owner' | 'member';
  businessName: string;
  logoURL: string | null;
  invitedByName: string | null;
  invitedByPhotoURL: string | null;
  primaryAccountHolderName: string;
  accountExists: boolean;
  existingAccountRole: string | null;
  eligible?: boolean;
  ineligibleReason?: string | null;
};

async function apiFetchWithTimeout<T = any>(
  path: string,
  options: RequestInit = {},
  timeoutMs = 15000,
): Promise<T> {
  const controller = new AbortController();
  const timeoutHandle = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await apiFetch<T>(path, { ...options, signal: controller.signal });
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      throw new globalThis.Error(
        `Request timed out after ${Math.ceil(timeoutMs / 1000)}s`,
      );
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutHandle);
  }
}

export default function JoinTeamPage() {
  const { user, refreshRole } = useAuth();
  const nav = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const orgId = params.get('org') || '';
  const token = params.get('token') || '';

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forceSignIn, setForceSignIn] = useState(false);
  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<InviteStatus>('loading');
  const [msg, setMsg] = useState('Checking invitation link...');
  const [err, setErr] = useState('');
  const acceptRequestKeyRef = useRef('');

  const invitedEmailLower = useMemo(
    () => (preview?.invitedEmail ?? '').trim().toLowerCase(),
    [preview],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      if (!orgId || !token) {
        if (!active) return;
        setStatus('error');
        setMsg('This invitation link is invalid or incomplete.');
        return;
      }

      try {
        const data = await apiFetch<InvitePreview>(
          `/api/invites/preview?org=${encodeURIComponent(orgId)}&token=${encodeURIComponent(token)}`,
        );
        if (!active) return;
        setPreview({
          ...data,
          invitedRole: data.invitedRole === 'owner' ? 'owner' : 'member',
        });
        setEmail(data.invitedEmail ?? '');
        acceptRequestKeyRef.current = '';
        setForceSignIn(false);
        setAutoAcceptEnabled(true);
        setErr('');
        setStatus('ready');
        setMsg(
          data.eligible === false
            ? (data.ineligibleReason || 'This account cannot accept this invitation.')
            : data.accountExists
            ? 'Sign in with the invited account to accept this invitation.'
            : 'Create your account to accept this invitation.',
        );
      } catch (e: any) {
        if (!active) return;
        setStatus('error');
        setMsg(String(e?.message || 'Could not load invitation details.'));
      }
    })();
    return () => {
      active = false;
    };
  }, [orgId, token]);

  useEffect(() => {
    if (!user || !preview) return;
    if (!autoAcceptEnabled) return;
    if (preview.eligible === false) return;

    const signedInEmail = (user.email ?? '').trim().toLowerCase();
    if (invitedEmailLower && signedInEmail && invitedEmailLower !== signedInEmail) {
      acceptRequestKeyRef.current = '';
      setAutoAcceptEnabled(false);
      setErr(
        `This invite is for ${preview.invitedEmail}. You are signed in as ${user.email || 'another account'}.`,
      );
      setMsg('Sign in with the invited email to continue.');
      return;
    }

    const requestKey = `${orgId}:${token}:${user.uid}`;
    if (acceptRequestKeyRef.current === requestKey) return;
    acceptRequestKeyRef.current = requestKey;

    let active = true;
    (async () => {
      setStatus('accepting');
      setErr('');
      setMsg('Accepting your invitation...');
      try {
        await apiFetchWithTimeout('/api/invites/accept', {
          method: 'POST',
          body: JSON.stringify({ orgId, token }),
        }, 15000);
        if (!active) return;
        void refreshRole().catch((refreshError) => {
          console.warn('Invite accepted but profile refresh failed:', refreshError);
        });
        setStatus('accepted');
        setMsg('Invitation accepted. Redirecting to your workspace...');
        setTimeout(() => nav('/dashboard/home', { replace: true }), 900);
      } catch (e: any) {
        if (!active) return;
        try {
          await apiFetchWithTimeout(`/api/team/members?orgId=${encodeURIComponent(orgId)}`, {}, 5000);
          if (!active) return;
          void refreshRole().catch((refreshError) => {
            console.warn('Invite membership verified but profile refresh failed:', refreshError);
          });
          setStatus('accepted');
          setMsg('Invitation accepted. Redirecting to your workspace...');
          setTimeout(() => nav('/dashboard/home', { replace: true }), 900);
          return;
        } catch {
          // Fall through to surface the original accept error.
        }
        acceptRequestKeyRef.current = '';
        setAutoAcceptEnabled(false);
        setStatus('ready');
        setErr(String(e?.message || 'Could not accept invitation. Please retry.'));
        setMsg('Unable to complete invite acceptance.');
      }
    })();

    return () => {
      active = false;
    };
  }, [user, preview, invitedEmailLower, orgId, token, nav, autoAcceptEnabled, refreshRole]);

  const handleCreateAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !preview) return;
    if (preview.eligible === false) {
      setErr(preview.ineligibleReason || 'This account cannot accept this invitation.');
      return;
    }
    setBusy(true);
    setErr('');

    try {
      const targetEmail = invitedEmailLower || email.trim().toLowerCase();
      if (!targetEmail) {
        setErr('Invitation email is missing.');
        return;
      }

      const policyError = getPasswordPolicyError(password);
      if (policyError) {
        setErr(policyError);
        return;
      }
      if (password !== confirmPassword) {
        setErr('Password and confirmation must match.');
        return;
      }

      const credential = await createUserWithEmailAndPassword(auth, targetEmail, password);
      try {
        await sendEmailVerification(credential.user, {
          url: `${window.location.origin}/verify-email`,
          handleCodeInApp: true,
        });
      } catch {
        // Invite acceptance does not block on email verification delivery.
      }
      acceptRequestKeyRef.current = '';
      setAutoAcceptEnabled(true);
      setPassword('');
      setConfirmPassword('');
      setStatus('ready');
      setMsg('Account created. Finishing invitation acceptance...');
    } catch (e: any) {
      const message = String(e?.message || 'Could not create account.');
      if (message.toLowerCase().includes('email-already-in-use')) {
        setForceSignIn(true);
        setPassword('');
        setConfirmPassword('');
        setAutoAcceptEnabled(false);
        setErr('An account already exists for this email. Sign in to accept the invitation.');
        setMsg('Sign in with the invited account to accept this invitation.');
      } else {
        setErr(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (preview?.eligible === false) {
      setErr(preview.ineligibleReason || 'This account cannot accept this invitation.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const targetEmail = invitedEmailLower || email.trim().toLowerCase();
      await signInWithEmailAndPassword(auth, targetEmail, password);
      acceptRequestKeyRef.current = '';
      setAutoAcceptEnabled(true);
      setPassword('');
      setMsg('Signed in. Finishing invitation acceptance...');
    } catch (e: any) {
      setErr(String(e?.message || 'Could not sign in.'));
    } finally {
      setBusy(false);
    }
  };

  const handleUseDifferentAccount = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await signOut(auth);
      setStatus('ready');
      setAutoAcceptEnabled(false);
      setMsg('Sign in with the invited email to continue.');
    } catch (e: any) {
      setErr(String(e?.message || 'Could not sign out.'));
    } finally {
      setBusy(false);
    }
  };

  const requiresSignIn = Boolean(preview?.accountExists || forceSignIn);
  const inviteIneligible = preview?.eligible === false;
  const signedInEmail = (user?.email ?? '').trim().toLowerCase();
  const emailMismatch =
    Boolean(user && invitedEmailLower && signedInEmail && invitedEmailLower !== signedInEmail);

  return (
    <Wrap>
      <JoinCard>
        <Header>
          <h2>Join Organization Workspace</h2>
          <p>{msg}</p>
        </Header>

        {preview ? (
          <InviteMeta>
            <Logo>
              {preview.logoURL ? (
                <LogoImage src={preview.logoURL} alt={`${preview.businessName} logo`} />
              ) : (
                (preview.businessName || 'O').slice(0, 1).toUpperCase()
              )}
            </Logo>
            <MetaBody>
              <BusinessName>{preview.businessName}</BusinessName>
              <InviterRow>
                <PortalAvatar
                  size={30}
                  name={preview.invitedByName || preview.primaryAccountHolderName}
                  photoURL={preview.invitedByPhotoURL}
                  disableNavigation
                  implicitSelfFallback={false}
                />
                <MetaLine>
                  Invited by {preview.invitedByName || preview.primaryAccountHolderName}
                </MetaLine>
              </InviterRow>
              <MetaLine>
                Invite sent to <strong>{preview.invitedEmail || 'Unknown email'}</strong>
              </MetaLine>
              <Badge>{preview.invitedRole === 'member' ? 'Organization member' : preview.invitedRole}</Badge>
            </MetaBody>
          </InviteMeta>
        ) : null}

        {status === 'ready' && preview ? (
          <>
            {inviteIneligible ? (
              <Error>{preview.ineligibleReason || 'This invitation cannot be accepted by this account type.'}</Error>
            ) : null}
            {!user ? (
              <>
                {requiresSignIn && !inviteIneligible ? (
                  <Form onSubmit={handleSignIn}>
                    <FieldLabel>
                      Invited email
                      <ReadOnlyInput
                        type="email"
                        value={preview.invitedEmail ?? email}
                        onChange={(event) => setEmail(event.target.value)}
                        readOnly={Boolean(preview.invitedEmail)}
                        autoComplete="email"
                        required
                      />
                    </FieldLabel>
                    <FieldLabel>
                      Password
                      <Input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        autoComplete="current-password"
                        required
                      />
                    </FieldLabel>
                    {err ? <Error>{err}</Error> : null}
                    <Button type="submit" disabled={busy}>
                      {busy ? 'Signing in...' : 'Sign in and accept invite'}
                    </Button>
                  </Form>
                ) : !inviteIneligible ? (
                  <Form onSubmit={handleCreateAccount}>
                    <FieldLabel>
                      Invited email
                      <ReadOnlyInput
                        type="email"
                        value={preview.invitedEmail ?? email}
                        onChange={(event) => setEmail(event.target.value)}
                        readOnly={Boolean(preview.invitedEmail)}
                        autoComplete="email"
                        required
                      />
                    </FieldLabel>
                    <FieldLabel>
                      Create password
                      <Input
                        type="password"
                        placeholder="Create password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        autoComplete="new-password"
                        required
                      />
                    </FieldLabel>
                    <FieldLabel>
                      Confirm password
                      <Input
                        type="password"
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        autoComplete="new-password"
                        required
                      />
                    </FieldLabel>
                    {err ? <Error>{err}</Error> : null}
                    <Button type="submit" disabled={busy}>
                      {busy ? 'Creating account...' : 'Create account and accept invite'}
                    </Button>
                  </Form>
                ) : null}
              </>
            ) : (
              <>
                <Divider />
                <Note>
                  Signed in as <strong>{user.email || '--'}</strong>.
                </Note>
                {inviteIneligible ? (
                  <Button type="button" onClick={handleUseDifferentAccount} disabled={busy}>
                    {busy ? 'Switching...' : 'Use a different account'}
                  </Button>
                ) : emailMismatch ? (
                  <>
                    {err ? <Error>{err}</Error> : null}
                    <Button type="button" onClick={handleUseDifferentAccount} disabled={busy}>
                      {busy ? 'Switching...' : 'Use invited email instead'}
                    </Button>
                  </>
                ) : (
                  <>
                    {err ? (
                      <>
                        <Error>{err}</Error>
                        <Button
                          type="button"
                          onClick={() => {
                            setErr('');
                            setAutoAcceptEnabled(true);
                            setStatus('ready');
                          }}
                          disabled={busy}
                        >
                          Retry invite acceptance
                        </Button>
                      </>
                    ) : (
                      <>
                        <Note>Completing invite acceptance now...</Note>
                        {!autoAcceptEnabled ? (
                          <Button
                            type="button"
                            onClick={() => {
                              acceptRequestKeyRef.current = '';
                              setAutoAcceptEnabled(true);
                            }}
                            disabled={busy}
                          >
                            Continue invite acceptance
                          </Button>
                        ) : null}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : null}

        {status === 'accepting' ? <Note>Please wait while we complete your invite...</Note> : null}
        {status === 'accepted' ? <Note>Taking you to your dashboard...</Note> : null}
        {status === 'error' ? <Error>{msg}</Error> : null}
      </JoinCard>
    </Wrap>
  );
}
